import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import React, { useCallback, useEffect, useState } from 'react'

import { API_URL, fetchOptions } from 'store/fetchUtils'
import styles from './FileViewer.module.sass'

const downloadFile = (fileData, fileName) => {
	const link = document.createElement('a')
	link.href = fileData
	link.setAttribute('download', fileName)
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

const FileViewer = (props) => {
	const { className, file } = props
	const { i18n, expireSession } = useGlobal()
	const [{ fileData, isImage, isPdf }, setFile] = useState({})
	const [error, setError] = useState(false)

	useEffect(() => {
		if (!file.id) {
			setError(true)
			return
		}

		let aborted
		const url = `${API_URL}/api/files/${file.id}`
		const controller = new AbortController()
		fetch(
			url,
			fetchOptions({
				signal: controller.signal,
			})
		)
			.then((r) => {
				if (r.status >= 400) {
					const error = new Error(r.statusText)
					error.status = r.status
					throw error
				} else {
					return r
				}
			})
			.then((response) => response.blob())
			.then((blob) => {
				const ext = (file.name || '').split('.').pop().toLowerCase()
				const extMap = {
					jpg: 'image/jpeg',
					jpe: 'image/jpeg',
					jpeg: 'image/jpeg',
					png: 'image/png',
					gif: 'image/gif',
					webp: 'image/webp',
					bmp: 'image/bmp',
					svg: 'image/svg+xml',
					pdf: 'application/pdf',
				}
				const type = extMap[ext] || blob.type || ''
				const isImage = !!type.match(/^image\//i)
				const isPdf = type === 'application/pdf'
				const fileData = window.URL.createObjectURL(blob)
				setFile({ fileData, isImage, isPdf })
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				}
				if (!aborted) {
					setError(true)
				}
			})
		return () => {
			controller.abort()
			aborted = true
		}
	}, [file.id, expireSession])

	return (
		<div className={className}>
			<div className={styles['root']}>
				{fileData && (
					<div className={styles['header']}>
						<button
							onClick={(e) => {
								e.preventDefault()
								downloadFile(fileData, file.name)
							}}
							className={styles['button']}
						>
							{i18n`Download`}
						</button>
						{file.externalLink && (
							<a
								href={file.externalLink}
								target='_blank'
								rel='noreferrer'
							>
								<Icon
									className={`${styles['file-icon']}`}
									name='x-ray'
									width='22px'
									height='22px'
								/>
							</a>
						)}
						<small className={styles['info-text']}>
							{error &&
								i18n`An error has occurred. Please download the file.`}
						</small>
					</div>
				)}
				<div className={styles['viewer']}>
					{!fileData && (
						<div className={styles['error-message']}>
							<small>
								{error
									? i18n`An error occur. Try again later.`
									: i18n`Loading`}
							</small>
						</div>
					)}
					{fileData && isImage && (
						<div className={styles['scrollable']}>
							<img
								src={fileData}
								alt={i18n`File content`}
								width='100%'
							/>
						</div>
					)}
					{fileData && isPdf && (
						<iframe
							className={styles['pdf-iframe']}
							src={fileData}
							title={file.name}
						/>
					)}
					{fileData && !isImage && !isPdf && (
						<div className={styles['error-message']}>
							<small>{i18n`Unsupported file type. Please download the file.`}</small>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default FileViewer
